import { normalizeGender } from "../utils/genderNormalize";
import { resolveIndianCity } from "./indianCities";

export const PROFILE_NAV_SECTIONS = [
  { id: "overview", label: "Profile Overview", scrollTo: "overview", icon: "overview" },
  { id: "basic", label: "Account & Security", scrollTo: "basic", icon: "shield" },
  { id: "summary", label: "Professional Summary", scrollTo: "summary", icon: "summary" },
  { id: "documents", label: "Documents", scrollTo: "documents", icon: "doc" },
  { id: "certifications", label: "Certificates & Training", scrollTo: "certifications", icon: "cert" },
  { id: "employment", label: "Experience & Logbook", scrollTo: "employment", icon: "work" },
  { id: "skills", label: "Skills & Competencies", scrollTo: "skills", icon: "skills" },
  { id: "clinical", label: "Clinical Experience", scrollTo: "clinical", icon: "clinical" },
  { id: "availability", label: "Availability & Preferences", scrollTo: "availability", icon: "calendar" },
  { id: "languages", label: "Languages", scrollTo: "languages", icon: "lang" },
  { id: "career", label: "Career Goals", scrollTo: "career", icon: "goal" },
  { id: "score", label: "Ratings & Reviews", scrollTo: "score", icon: "star" },
  { id: "references", label: "References", scrollTo: "references", icon: "ref" },
  { id: "video", label: "Video Introduction", scrollTo: "video", icon: "video" },
  { id: "premiumHourly", label: "Premium Hourly Readiness", scrollTo: "premiumHourly", icon: "premium" },
  { id: "verification", label: "Verification & Compliance", scrollTo: "verification", icon: "user" },
];

/** Physio compliance — only fields not already on the main caregiver profile. */
export const PHYSIO_COMPLIANCE_NAV_SECTIONS = [
  { id: "complianceOnboarding", label: "Compliance Onboarding", scrollTo: "complianceOnboarding", icon: "shield" },
];

export function getProfileNavSections(isPhysio = false) {
  if (!isPhysio) return PROFILE_NAV_SECTIONS;
  const verificationIdx = PROFILE_NAV_SECTIONS.findIndex((s) => s.scrollTo === "verification");
  const insertAt = verificationIdx >= 0 ? verificationIdx : PROFILE_NAV_SECTIONS.length;
  return [
    ...PROFILE_NAV_SECTIONS.slice(0, insertAt),
    ...PHYSIO_COMPLIANCE_NAV_SECTIONS,
    ...PROFILE_NAV_SECTIONS.slice(insertAt),
  ];
}

export const PROFILE_SECTIONS = [
  { id: "basic", label: "Basic Information", number: 1 },
  { id: "summary", label: "Professional Summary", number: 2 },
  { id: "verification", label: "Verification & Compliance", number: 3 },
  { id: "score", label: "Professional Score", number: 4 },
  { id: "keyDetails", label: "Key Details", number: 5 },
  { id: "documents", label: "Documents", number: 6 },
  { id: "certifications", label: "NEMA Certifications", number: 7 },
  { id: "employment", label: "Employment History", number: 8 },
  { id: "clinical", label: "Clinical Experience (Logbook)", number: 9 },
  { id: "skills", label: "Skills & Competencies", number: 10 },
  { id: "languages", label: "Languages", number: 11 },
  { id: "availability", label: "Availability & Preferences", number: 12 },
  { id: "career", label: "Career Goals", number: 13 },
  { id: "video", label: "Video Introduction", number: 14 },
  { id: "references", label: "References", number: 15 },
  { id: "premiumHourly", label: "Premium Hourly Readiness", number: 16 },
];

/** Guided onboarding wizard steps (complete-profile). */
export const PROFILE_WIZARD_STEPS = [
  {
    id: 1,
    label: "Basic Info",
    title: "Let's start with the basics",
    hint: "This information appears on your public profile.",
    sections: ["basic", "keyDetails"],
  },
  {
    id: 2,
    label: "Your Role",
    title: "Which role best describes you?",
    hint: "Your onboarding will be tailored based on this.",
    sections: ["role"],
  },
  {
    id: 3,
    label: "Qualifications",
    title: "Your qualifications",
    sections: ["qualifications"],
  },
  {
    id: 4,
    label: "Experience",
    title: "Professional experience",
    hint: "Your work history helps us match you with the right families and hospitals.",
    sections: ["background", "employment"],
  },
  {
    id: 5,
    label: "Skills & Specializations",
    title: "Skills & Specializations",
    hint: "Choose everything you're proficient in.",
    sections: ["skills"],
  },
  {
    id: 6,
    label: "Documents",
    title: "Upload verification documents",
    hint: "Clear documents help NEMA Care verify your identity faster.",
    sections: ["documents"],
  },
  {
    id: 7,
    label: "Certificates",
    title: "Certificates & Trainings",
    hint: "Upload any BLS, ACLS, specialty course or on-the-job trainings.",
    sections: ["certifications"],
  },
  {
    id: 8,
    label: "Languages",
    title: "Languages you speak",
    hint: "Families prefer caregivers who can converse comfortably.",
    sections: ["background", "languages"],
  },
  {
    id: 9,
    label: "Availability",
    title: "Availability & Work Preferences",
    hint: "Tell families how and when you can work.",
    sections: ["availability"],
  },
  {
    id: 10,
    label: "Summary",
    title: "Professional Summary",
    hint: "Choose a starter template or write your own. You can edit the text before continuing.",
    sections: ["summary"],
  },
  {
    id: 11,
    label: "Photo & Video",
    title: "Add a photo and short video introduction",
    hint: "A friendly 30-60 sec video boosts trust by up to 40%.",
    sections: ["photo", "video"],
  },
  {
    id: 12,
    label: "Review",
    title: "Final review",
    hint: "Verify your details before submitting for admin approval.",
    sections: ["review"],
  },
];

export function isProfileWizardSectionVisible(sectionId, wizardStep) {
  const step = PROFILE_WIZARD_STEPS.find((s) => s.id === Number(wizardStep));
  if (!step) return true;
  return step.sections.includes(sectionId);
}

export const KEY_DETAIL_FIELDS = [
  { field: "bloodGroup", label: "Blood Group", icon: "🩸" },
  { field: "height", label: "Height", icon: "📏" },
  { field: "religion", label: "Religion", icon: "🙏" },
  { field: "weight", label: "Weight (kg)", icon: "⚖️" },
  { field: "nationality", label: "Nationality", icon: "🌐" },
  { field: "experience", label: "Experience (yrs)", icon: "💼" },
  { field: "maritalStatus", label: "Marital Status", icon: "💍" },
  { field: "drivingLicense", label: "Driving License", icon: "🚗" },
];

export const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export const RELIGION_DETAIL_OPTIONS = [
  "Hindu",
  "Muslim",
  "Christian",
  "Sikh",
  "Jain",
  "Buddhist",
  "Parsi",
  "Jewish",
  "Other",
];

export const NATIONALITY_OPTIONS = ["Indian", "Others"];

export const MARITAL_STATUS_OPTIONS = ["Married", "Unmarried", "Widow"];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const HEIGHT_FEET_MIN = 1;
export const HEIGHT_FEET_MAX = 7;
export const HEIGHT_INCH_MIN = 1;
export const HEIGHT_INCH_MAX = 11;
/** Legacy total-inches range (used when converting old Inches / cm values). */
export const HEIGHT_TOTAL_INCH_MIN = 12;
export const HEIGHT_TOTAL_INCH_MAX = HEIGHT_FEET_MAX * 12 + HEIGHT_INCH_MAX;
export const HEIGHT_CM_MIN = 100;
export const HEIGHT_CM_MAX = 250;
export const WEIGHT_KG_MIN = 45;
export const WEIGHT_KG_MAX = 100;
export const EXPERIENCE_YEARS_MIN = 0;
export const EXPERIENCE_YEARS_MAX = 25;

function clampHeightFeet(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return Math.max(HEIGHT_FEET_MIN, Math.min(HEIGHT_FEET_MAX, Math.round(n)));
}

function clampRemainderInches(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return Math.max(HEIGHT_INCH_MIN, Math.min(HEIGHT_INCH_MAX, Math.round(n)));
}

function ftInFromTotalInches(total) {
  const n = Math.round(Number(total));
  if (!Number.isFinite(n) || n <= 0) return { feet: "", inches: "" };
  let feet = Math.floor(n / 12);
  let inches = n % 12;
  if (feet > HEIGHT_FEET_MAX) {
    feet = HEIGHT_FEET_MAX;
    inches = HEIGHT_INCH_MAX;
  }
  if (feet < HEIGHT_FEET_MIN) return { feet: "", inches: "" };
  return {
    feet,
    inches: inches > 0 ? inches : "",
  };
}

export function formatKeyDetailHeight({ feet, inches }) {
  const ft = clampHeightFeet(feet);
  const inch = clampRemainderInches(inches);
  if (!ft) return "";
  if (inch) return `${ft} ft ${inch} in`;
  return `${ft} ft`;
}

/** Parse stored height string / structured fields into feet + remainder inches. */
export function parseKeyDetailHeight(key = {}) {
  const unitRaw = String(key.heightUnit || "").toLowerCase();
  const hasFeetField = key.heightFeet != null && key.heightFeet !== "";
  const hasInchesField = key.heightInches != null && key.heightInches !== "";
  const empty = { unit: "", feet: "", inches: "", cm: "" };

  function asFtIn(feet, inches) {
    const ft = clampHeightFeet(feet);
    const inch = clampRemainderInches(inches);
    if (!ft && !inch) return empty;
    return { unit: "ft", feet: ft || "", inches: inch || "", cm: "" };
  }

  if (unitRaw === "cm") {
    const cmVal =
      key.heightCm != null && key.heightCm !== ""
        ? Number(key.heightCm)
        : Number(String(key.height || "").replace(/[^\d.]/g, ""));
    if (Number.isFinite(cmVal) && cmVal > 0) {
      const converted = ftInFromTotalInches(cmVal / 2.54);
      return asFtIn(converted.feet, converted.inches);
    }
  }

  if (hasFeetField || unitRaw === "ft" || unitRaw === "feet" || unitRaw === "foot") {
    const inchNum = hasInchesField ? Number(key.heightInches) : NaN;
    if (Number.isFinite(inchNum) && inchNum > HEIGHT_INCH_MAX) {
      const converted = ftInFromTotalInches(Number(key.heightFeet || 0) * 12 + inchNum);
      return asFtIn(converted.feet, converted.inches);
    }
    return asFtIn(key.heightFeet, hasInchesField ? key.heightInches : "");
  }

  if (unitRaw === "in" || unitRaw === "inch" || unitRaw === "inches" || hasInchesField) {
    const inches =
      hasInchesField
        ? Number(key.heightInches)
        : Number(String(key.height || "").replace(/[^\d.]/g, ""));
    if (Number.isFinite(inches) && inches <= HEIGHT_INCH_MAX) {
      return asFtIn(hasFeetField ? key.heightFeet : "", inches);
    }
    const converted = ftInFromTotalInches(inches);
    return asFtIn(converted.feet, converted.inches);
  }

  const raw = String(key.height || "").trim();
  if (!raw) return empty;

  const ftInWords = raw.match(/^(\d+)\s*(?:ft|feet)\s+(\d+)\s*(?:in|inch|inches|["″])?$/i);
  if (ftInWords) return asFtIn(ftInWords[1], ftInWords[2]);

  const inOnly = raw.match(/^(\d+)\s*(?:in|inch|inches|["″])$/i);
  if (inOnly) {
    const converted = ftInFromTotalInches(inOnly[1]);
    return asFtIn(converted.feet, converted.inches);
  }

  const cmMatch = raw.match(/^(\d+(?:\.\d+)?)\s*cm$/i);
  if (cmMatch) {
    const converted = ftInFromTotalInches(Number(cmMatch[1]) / 2.54);
    return asFtIn(converted.feet, converted.inches);
  }

  const ftInMatch = raw.match(/^(\d+)\s*['′]\s*(\d+)\s*(?:["″]|in(?:ch(?:es)?)?)?$/i);
  if (ftInMatch) return asFtIn(ftInMatch[1], ftInMatch[2]);

  const ftOnly = raw.match(/^(\d+)\s*(?:ft|feet|')$/i);
  if (ftOnly) return asFtIn(ftOnly[1], "");

  const plainNum = Number(raw);
  if (Number.isFinite(plainNum) && plainNum >= 100) {
    const converted = ftInFromTotalInches(plainNum / 2.54);
    return asFtIn(converted.feet, converted.inches);
  }

  return empty;
}

export function clampExperienceYears(value) {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(Math.max(EXPERIENCE_YEARS_MIN, Math.min(EXPERIENCE_YEARS_MAX, Math.round(n))));
}

export function clampWeightKg(value) {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(Math.max(WEIGHT_KG_MIN, Math.min(WEIGHT_KG_MAX, Math.round(n))));
}

export const RATING_BREAKDOWN = [
  { key: "professionalism", label: "Professionalism" },
  { key: "communication", label: "Communication" },
  { key: "compassion", label: "Compassion" },
  { key: "punctuality", label: "Punctuality" },
  { key: "satisfaction", label: "Family Satisfaction" },
];

export const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
  "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

/** Map free-text / city labels like "Bengaluru, Karnataka" onto INDIAN_STATES. */
export function matchIndianState(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower === "ncr" || lower === "delhi ncr" || lower === "new delhi" || lower === "nct of delhi") {
    return "Delhi";
  }
  const exact = INDIAN_STATES.find((s) => s.toLowerCase() === lower);
  if (exact) return exact;
  return (
    INDIAN_STATES.find(
      (s) => lower.includes(s.toLowerCase()) || s.toLowerCase().includes(lower)
    ) || ""
  );
}

/**
 * City name only from labels like "RBI Layout, Bengaluru" or catalog areas.
 * Never returns "Area, City" — only the city portion for the Current City field.
 */
export function extractCityOnly(cityOrLocation) {
  const raw = String(cityOrLocation || "").trim();
  if (!raw) return "";

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const tailState = matchIndianState(parts[parts.length - 1]);
    if (tailState) return parts[parts.length - 2] || "";
    // "Area, City" marketplace labels — last segment is the city.
    const cityPart = parts[parts.length - 1];
    const cityResolved = resolveIndianCity(cityPart);
    if (cityResolved?.city) return cityResolved.city;
    const resolved = resolveIndianCity(raw);
    if (resolved?.city) return resolved.city;
    return cityPart;
  }

  const resolved = resolveIndianCity(raw);
  if (resolved?.city) return resolved.city;
  return parts[0] || raw;
}

/**
 * Infer state from city/location text already collected at signup
 * (e.g. "Bengaluru, Karnataka" or "HSR Layout, Bengaluru"). Keeps an existing valid state when present.
 */
export function inferStateFromCity(cityOrLocation, existingState = "") {
  const existing = matchIndianState(existingState);
  if (existing) return existing;

  const raw = String(cityOrLocation || "").trim();
  if (!raw) return "";

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const fromTail = matchIndianState(parts[parts.length - 1]);
    if (fromTail) return fromTail;
  }

  const resolved = resolveIndianCity(raw);
  if (resolved?.state) {
    const fromCatalog = matchIndianState(resolved.state);
    if (fromCatalog) return fromCatalog;
  }

  const cityOnly = extractCityOnly(raw);
  if (cityOnly && cityOnly.toLowerCase() !== raw.toLowerCase()) {
    const cityResolved = resolveIndianCity(cityOnly);
    if (cityResolved?.state) {
      const fromCity = matchIndianState(cityResolved.state);
      if (fromCity) return fromCity;
    }
  }

  return matchIndianState(raw) || matchIndianState(cityOnly) || "";
}

/** Keep caregiver login mobile as 10-digit Indian number (no +91 prefix in form). */
export function normalizeIndianMobile(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 10) return digits;
  if (digits.startsWith("91") && digits.length >= 12) return digits.slice(-10);
  return digits.slice(-10);
}

/**
 * Parse city/state hints from free-text address or city input.
 * Example: "12 MG Road, Indiranagar, Bengaluru, Karnataka" → city Bengaluru, state Karnataka.
 */
export function parseLocationFromText(text, existing = {}) {
  const raw = String(text || "").trim();
  const city = String(existing.city || "").trim();
  const state = inferStateFromCity(raw, existing.state || "");
  if (!raw) return { city, state };

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const tailState = matchIndianState(parts[parts.length - 1]);
    if (tailState) {
      return {
        city: parts[parts.length - 2] || city,
        state: tailState,
      };
    }
  }

  const inferredState = inferStateFromCity(raw, state);
  const resolvedCity =
    city ||
    (parts.length === 1 && !matchIndianState(parts[0]) ? parts[0] : city);

  return {
    city: resolvedCity,
    state: inferredState || state,
  };
}

/** Merge basic contact/location fields. City changes may infer state when state is empty. */
export function syncBasicContactFields(basic, field, value) {
  const next = { ...basic };

  if (field === "mobile") {
    next.mobile = normalizeIndianMobile(value);
    return next;
  }

  if (field === "city") {
    next.city = String(value || "");
    next.state = inferStateFromCity(value || next.city, next.state);
    return next;
  }

  if (field === "state") {
    next.state = matchIndianState(value) || value;
    return next;
  }

  if (field === "address") {
    next.address = value;
    return next;
  }

  next[field] = value;
  return next;
}

/** Normalize contact/location fields before persisting caregiver profile. */
export function normalizeBasicForSave(basic = {}) {
  const cityOnly = extractCityOnly(basic.city) || String(basic.city || "").trim();
  return {
    ...basic,
    mobile: normalizeIndianMobile(basic.mobile),
    city: cityOnly,
    state: inferStateFromCity(cityOnly || basic.city, basic.state),
    address: String(basic.address || "").trim(),
  };
}

/**
 * Resolve Current City (city name only) + State from profile / onboarding location text.
 * Prefers saved profile city/location over onboarding account fields.
 */
export function bindCityStateFromLogin(sources = {}) {
  const loginLocation =
    sources.city ||
    sources.location ||
    sources.accountCity ||
    sources.accountLocation ||
    "";
  const city = extractCityOnly(loginLocation);
  const state =
    matchIndianState(sources.state) ||
    inferStateFromCity(loginLocation || city, sources.state || "");
  return { city, state, loginLocation: String(loginLocation || "").trim() };
}

export const DOCUMENT_TYPES = [
  { id: "aadhaarFront", label: "Aadhaar — Front", required: true },
  { id: "aadhaarBack", label: "Aadhaar — Back", required: true },
  { id: "pan", label: "PAN Card", required: true },
  { id: "passport", label: "Passport" },
  { id: "drivingLicense", label: "Driving License" },
  { id: "police", label: "Police Verification" },
  { id: "medicalFitness", label: "Medical Fitness" },
  { id: "vaccination", label: "Vaccination Certificate", required: false },
];

/** Physiotherapist CareHub verification docs (Platform Paradigm) — collected in physio compliance flow. */
export const PHYSIO_DOCUMENT_TYPES = [
  { id: "degree", label: "Degree Certificate", required: true },
  { id: "iap", label: "IAP Registration", required: true },
  { id: "gst", label: "GST Certificate", required: false },
  { id: "pan", label: "PAN Card", required: true },
];

export function getDocumentTypesForServiceRole(serviceRole) {
  // Caregiver onboarding verification grid uses the shared document set,
  // plus role-specific additions (e.g. Nursing Registration Certificate for nurses).
  // Physio-specific Degree / IAP / GST uploads are handled in the physio compliance flow.
  const key = String(serviceRole || "").trim().toLowerCase();
  if (key === "nurses" || key === "nurse") {
    return [
      ...DOCUMENT_TYPES,
      { id: "nursingRegistrationCertificate", label: "Nursing Registration Certificate", required: true },
    ];
  }
  return DOCUMENT_TYPES;
}

export const NEMA_CERTIFICATIONS = [
  "Module 1: Personal Hygiene & ADL",
  "Module 2: Vital Signs Monitoring",
  "Module 3: Medication Assistance",
  "Module 4: Mobility & Transfer",
  "Module 5: Dementia & Cognitive Care",
  "Module 6: Post-Surgical Care",
  "Module 7: Palliative & End-of-Life Care",
  "Module 8: Infection Control",
  "Module 9: Emergency Response",
  "Module 10: Nutrition & Feeding",
  "Module 11: Communication & Family Coordination",
];

export const CLINICAL_CARE_TYPES = [
  "Dementia Care",
  "Bedridden Care",
  "Stroke Rehabilitation",
  "Post-Surgical Care",
  "Palliative Care",
  "Parkinson's Care",
  "Diabetes Management",
  "Tracheostomy Care",
];

export const SKILL_COMPETENCIES = [
  "Personal Care",
  "Medication Assistance",
  "Mobility Assistance",
  "Wound Care",
  "Vital Signs Monitoring",
  "Feeding Assistance",
  "Dementia Support",
  "Companionship",
  "Exercise Assistance",
  "Catheter Care",
  "Oxygen Support",
  "Emergency Response",
];

/** Tap-to-select care specializations — attendant defaults (legacy export name). */
export const CARE_SPECIALIZATIONS = [
  "Elder Care",
  "Bedridden Care",
  "Post-Surgery Care",
  "Mobility Support",
  "Companion Care",
  "Palliative Care",
  "Diabetes Management",
  "Alzheimer / Dementia",
];

/** Tap-to-select clinical / technical skills — attendant defaults (legacy export name). */
export const CLINICAL_TECHNICAL_SKILLS = [
  "Personal Hygiene & ADL",
  "Feeding Assistance",
  "Mobility & Transfer",
  "Companion Support",
  "Vital Sign Basics",
  "Medication Reminders",
];

/** Role-scoped specialization chips for the Skills & Specializations step. */
export const SPECIALIZATIONS_BY_ROLE = {
  attendant: CARE_SPECIALIZATIONS,
  nurses: [
    "ICU / Critical Care",
    "Cardiac Care",
    "Neuro Care",
    "Oncology",
    "Wound Care",
    "Ryles Tube Feeding",
    "Tracheostomy Care",
    "PEG Tube Care",
    "Ventilator Care",
    "Home Nursing",
  ],
  physio: [
    "Ortho Rehab",
    "Neuro Rehab",
    "Stroke Rehab",
    "Post-Surgical Rehab",
    "Sports Injury",
    "Pediatric Physio",
    "Geriatric Physio",
    "Cardiac Rehab",
  ],
};

/** Role-scoped clinical / technical skill chips. */
export const CLINICAL_SKILLS_BY_ROLE = {
  attendant: CLINICAL_TECHNICAL_SKILLS,
  nurses: [
    "IV Cannulation",
    "Injection Administration",
    "Wound Dressing",
    "Catheterization",
    "Suctioning",
    "Ryles Tube",
    "Tracheostomy Care",
    "ECG Monitoring",
  ],
  physio: [
    "Manual Therapy",
    "Electrotherapy",
    "Gait Training",
    "Balance Training",
    "Kinesio Taping",
    "Therapeutic Exercise",
    "Ultrasound Therapy",
    "Dry Needling",
  ],
};

export function getSpecializationOptionsForRole(role) {
  const key = String(role || "").toLowerCase();
  if (key === "nurses" || key === "nurse") return SPECIALIZATIONS_BY_ROLE.nurses;
  if (key === "physio" || key === "physiotherapist") return SPECIALIZATIONS_BY_ROLE.physio;
  return SPECIALIZATIONS_BY_ROLE.attendant;
}

export function getClinicalSkillOptionsForRole(role) {
  const key = String(role || "").toLowerCase();
  if (key === "nurses" || key === "nurse") return CLINICAL_SKILLS_BY_ROLE.nurses;
  if (key === "physio" || key === "physiotherapist") return CLINICAL_SKILLS_BY_ROLE.physio;
  return CLINICAL_SKILLS_BY_ROLE.attendant;
}

export const LANGUAGE_LEVELS = ["Native", "Fluent", "Good", "Basic"];

/** Preset languages for onboarding Screen 8 (Excel flow). */
export const ONBOARDING_LANGUAGES = [
  "Hindi",
  "English",
  "Kannada",
  "Tamil",
  "Telugu",
  "Malayalam",
  "Marathi",
  "Bengali",
  "Gujarati",
  "Punjabi",
];

/** Same options as marketplace search shift dropdown. */
export const WORK_TYPES = [
  "Hourly",
  "12 hour - day shift",
  "12 hour - night shift",
  "24 hours live in",
];

/** Onboarding work-type chips (label shown in UI, value stored). */
export const WORK_TYPE_OPTIONS = [
  { value: "Hourly", label: "Hourly Visit" },
  { value: "12 hour - day shift", label: "Day Shift" },
  { value: "12 hour - night shift", label: "Night Shift" },
  { value: "24 hours live in", label: "24 Hrs Live-in" },
];

export function getWorkTypeOptionsForRole(role) {
  const key = String(role || "").toLowerCase();
  if (key === "physio" || key === "physiotherapist") {
    return WORK_TYPE_OPTIONS.filter((opt) => opt.value === "Hourly");
  }
  return WORK_TYPE_OPTIONS;
}

/** Selectable education/qualification levels, scoped to the caregiver's chosen care role. */
export const QUALIFICATION_OPTIONS_BY_ROLE = {
  attendant: [
    "10th Pass",
    "12th Pass",
    "Graduation",
    "Diploma in Home Care",
    "GNM (partial)",
    "Others",
  ],
  nurses: [
    "ANM",
    "GNM",
    "B.Sc Nursing",
    "M.Sc Nursing",
    "Post-basic B.Sc Nursing",
  ],
  physio: [
    "Diploma in Physiotherapy",
    "BPT",
    "MPT (Ortho)",
    "MPT (Neuro)",
    "MPT (Sports)",
    "MPT (Cardio)",
  ],
};

/** Registration number is required for clinical roles. */
export function isQualificationRegistrationRequired(role) {
  const key = String(role || "").toLowerCase();
  return key === "nurses" || key === "nurse" || key === "physio" || key === "physiotherapist";
}

export function getQualificationOptionsForRole(role) {
  const key = String(role || "").toLowerCase();
  if (key === "nurses" || key === "nurse") return QUALIFICATION_OPTIONS_BY_ROLE.nurses;
  if (key === "physio" || key === "physiotherapist") return QUALIFICATION_OPTIONS_BY_ROLE.physio;
  return QUALIFICATION_OPTIONS_BY_ROLE.attendant;
}

function normalizeSingleWorkType(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (WORK_TYPES.includes(raw)) return raw;
  const lower = raw.toLowerCase();
  if (lower.includes("hour") && !lower.includes("12") && !lower.includes("24")) return "Hourly";
  if (lower.includes("12") && (lower.includes("night") || lower.includes("night shift"))) {
    return "12 hour - night shift";
  }
  if (lower.includes("12") && (lower.includes("day") || lower.includes("day shift"))) {
    return "12 hour - day shift";
  }
  if (lower.includes("24") || lower.includes("live-in") || lower.includes("live in") || lower === "live-in") {
    return "24 hours live in";
  }
  if (lower === "full-time" || lower === "full time") return "24 hours live in";
  if (lower === "part-time" || lower === "part time" || lower === "live-out" || lower === "live out") {
    return "Hourly";
  }
  return raw;
}

/** Map legacy work-type values to current shift options and return a list for multi-select UI. */
export function normalizeWorkTypes(value) {
  const rawValues = (Array.isArray(value) ? value : [value])
    .flatMap((item) => String(item || "").split(","))
    .map((item) => item.trim())
    .filter(Boolean);
  const normalized = rawValues
    .map(normalizeSingleWorkType)
    .filter(Boolean)
    .filter((item, idx, arr) => arr.indexOf(item) === idx);
  return normalized;
}

/** Backward-compatible single value helper retained for older call sites. */
export function normalizeWorkType(value) {
  return normalizeWorkTypes(value)[0] || "";
}

export function serializeWorkTypes(value) {
  return normalizeWorkTypes(value).join(", ");
}

export function createEmptyProfile() {
  return {
    caregiverId: null,
    basic: {
      firstName: "",
      lastName: "",
      fullName: "",
      mobile: "",
      email: "",
      gender: "",
      dateOfBirth: "",
      city: "",
      state: "",
      pincode: "",
      address: "",
      profileImageUrl: "",
      profileImageStorageKey: "",
    },
    professional: {
      bio: "",
      experienceYears: "",
      skills: [],
      specializations: [],
      languages: [],
      shiftStartTime: "09:00",
      shiftEndTime: "17:00",
      salaryExpectation: "",
      hourlyRateExpectation: "",
    },
    verification: {},
    availability: {
      availableFrom: "",
      workType: [],
      willingToRelocate: false,
      preferredLocations: [],
    },
    videoIntroUrl: "",
    profileVisibility: "families",
    profileExtended: {
      keyDetails: {
        bloodGroup: "",
        height: "",
        heightUnit: "",
        heightFeet: "",
        heightInches: "",
        heightCm: "",
        religion: "",
        weight: "",
        nationality: "Indian",
        maritalStatus: "",
        drivingLicense: "",
      },
      documents: {},
      certifications: [],
      employmentHistory: [{ org: "", role: "", from: "", to: "", bullets: "", reasonForLeaving: "" }],
      currentEmployer: "",
      currentTitle: "",
      clinicalLogbook: CLINICAL_CARE_TYPES.map((careType) => ({
        careType,
        verifiedDays: 0,
      })),
      skillRatings: SKILL_COMPETENCIES.map((skill) => ({ skill, rating: 0 })),
      languageProficiency: [],
      careerGoals: { shortTerm: "", longTerm: "" },
      qualifications: {
        levels: [],
        institute: "",
        passingYear: "",
        registrationNumber: "",
      },
      references: [],
      marketplacePrefs: {
        serviceRole: "attendant",
        foodPreference: "",
        sleepArrangement: "",
        nurseQualifications: [],
        physioFrequency: "",
        physioDuration: "",
        patientConditions: [],
        nurseClinical: [],
        nurseSkills: [],
        physioRequirements: [],
      },
      physioDossier: null,
    },
  };
}

export function mergeProfileFromApi(data) {
  const base = createEmptyProfile();
  if (!data) return base;
  const ext = data.profileExtended || {};
  const basicIn = data.basic || {};
  // Legacy phone-OTP / role labels stored as fake names — leave the field empty.
  const rawFullName = String(basicIn.fullName || "").trim();
  const fullName = /^(caregiver|care\s+attendant|nurse|physiotherapist|physio|therapist)$/i.test(
    rawFullName
  )
    ? ""
    : rawFullName;
  const rawFirstName = String(basicIn.firstName || "").trim();
  const firstName = /^(caregiver|care\s+attendant|nurse|physiotherapist|physio|therapist)$/i.test(
    rawFirstName
  )
    ? ""
    : rawFirstName;
  const resolvedFullName =
    fullName ||
    [firstName, String(basicIn.lastName || "").trim()].filter(Boolean).join(" ").trim();
  const resolvedServiceRole =
    ext.marketplacePrefs?.serviceRole ||
    data.serviceRole ||
    base.profileExtended.marketplacePrefs.serviceRole;
  const mp = ext.marketplacePrefs || {};
  const languageNames = (ext.languageProficiency || [])
    .map((row) => String(row?.language || "").trim())
    .filter(Boolean);
  const professionalIn = data.professional || {};
  const specializations =
    Array.isArray(professionalIn.specializations) && professionalIn.specializations.length
      ? professionalIn.specializations
      : Array.isArray(ext.professionalSpecializations) && ext.professionalSpecializations.length
        ? ext.professionalSpecializations
        : resolvedServiceRole === "physio" && Array.isArray(mp.physioRequirements)
          ? mp.physioRequirements
          : resolvedServiceRole === "nurses" && Array.isArray(mp.nurseSkills)
            ? mp.nurseSkills
            : [];
  const qualificationLevels = Array.isArray(ext.qualifications?.levels)
    ? ext.qualifications.levels
    : Array.isArray(mp.nurseQualifications) && mp.nurseQualifications.length
      ? mp.nurseQualifications
      : [];
  const skills =
    Array.isArray(professionalIn.skills) && professionalIn.skills.length
      ? professionalIn.skills
      : resolvedServiceRole === "nurses" && Array.isArray(mp.nurseClinical)
        ? mp.nurseClinical
        : [];
  const prefWorkTypes = Array.isArray(mp.workTypes) ? mp.workTypes.filter(Boolean) : [];
  const boundWorkTypes = normalizeWorkTypes(
    prefWorkTypes.length ? prefWorkTypes : data.availability?.workTypes || data.availability?.workType || ""
  );
  const languages =
    Array.isArray(professionalIn.languages) && professionalIn.languages.length
      ? professionalIn.languages
      : languageNames;
  return {
    ...base,
    ...data,
    npid: data.npid || null,
    verificationBadges: Array.isArray(data.verificationBadges) ? data.verificationBadges : [],
    reviewCount: Number(data.reviewCount) || Number(data.scores?.reviewCount) || 0,
    trustStats: data.trustStats || null,
    careerLadder: Array.isArray(data.careerLadder) ? data.careerLadder : [],
    levelFramework: Array.isArray(data.levelFramework) ? data.levelFramework : [],
    levelDetails: data.levelDetails || null,
    complianceChecklist: Array.isArray(data.complianceChecklist) ? data.complianceChecklist : [],
    verified: Boolean(data.verified),
    designation: data.designation || null,
    publicRating: data.publicRating != null ? Number(data.publicRating) : null,
    serviceRole: resolvedServiceRole,
    basic: {
      ...base.basic,
      ...basicIn,
      firstName,
      fullName: resolvedFullName,
      gender: normalizeGender(basicIn.gender),
      mobile: normalizeIndianMobile(basicIn.mobile || basicIn.phone || basicIn.username || ""),
      city: extractCityOnly(basicIn.city || basicIn.location) || "",
      state: inferStateFromCity(
        basicIn.city || basicIn.location || basicIn.address,
        basicIn.state
      ),
      address: basicIn.address || "",
      pincode: basicIn.pincode || ext.keyDetails?.pincode || ext.physioDossier?.pincode || "",
    },
    professional: {
      ...base.professional,
      ...professionalIn,
      specializations,
      skills,
      languages,
    },
    availability: {
      ...base.availability,
      ...(data.availability || {}),
      workType: boundWorkTypes,
    },
    profileExtended: {
      ...base.profileExtended,
      ...ext,
      keyDetails: {
        ...base.profileExtended.keyDetails,
        ...(ext.keyDetails || {}),
        pincode:
          ext.keyDetails?.pincode ||
          basicIn.pincode ||
          ext.physioDossier?.pincode ||
          "",
      },
      careerGoals: { ...base.profileExtended.careerGoals, ...(ext.careerGoals || {}) },
      qualifications: {
        ...base.profileExtended.qualifications,
        ...(ext.qualifications || {}),
        levels: qualificationLevels,
      },
      certifications:
        Array.isArray(ext.certifications) && ext.certifications.length
          ? ext.certifications
          : base.profileExtended.certifications,
      employmentHistory: Array.isArray(ext.employmentHistory)
        ? ext.employmentHistory.map((job) => ({
            org:
              job?.org ||
              job?.employer ||
              job?.organization ||
              job?.employerName ||
              job?.employer_name ||
              "",
            role: job?.role || job?.title || "",
            from: job?.from || job?.startDate || job?.start_date || "",
            to: job?.to || job?.endDate || job?.end_date || "",
            bullets: job?.bullets || job?.summary || "",
            reasonForLeaving: job?.reasonForLeaving || "",
            city: job?.city || "",
          }))
        : base.profileExtended.employmentHistory,
      currentEmployer: ext.currentEmployer || base.profileExtended.currentEmployer || "",
      currentTitle: ext.currentTitle || base.profileExtended.currentTitle || "",
      clinicalLogbook:
        Array.isArray(ext.clinicalLogbook) && ext.clinicalLogbook.length
          ? ext.clinicalLogbook
          : base.profileExtended.clinicalLogbook,
      skillRatings:
        Array.isArray(ext.skillRatings) && ext.skillRatings.length
          ? ext.skillRatings
          : base.profileExtended.skillRatings,
      marketplacePrefs: {
        ...base.profileExtended.marketplacePrefs,
        ...mp,
        serviceRole: resolvedServiceRole,
        nurseQualifications:
          Array.isArray(mp.nurseQualifications) && mp.nurseQualifications.length
            ? mp.nurseQualifications
            : resolvedServiceRole === "nurses"
              ? qualificationLevels
              : [],
        nurseSkills:
          Array.isArray(mp.nurseSkills) && mp.nurseSkills.length
            ? mp.nurseSkills
            : resolvedServiceRole === "nurses"
              ? specializations
              : [],
        nurseClinical:
          Array.isArray(mp.nurseClinical) && mp.nurseClinical.length
            ? mp.nurseClinical
            : resolvedServiceRole === "nurses"
              ? skills
              : [],
        physioRequirements:
          Array.isArray(mp.physioRequirements) && mp.physioRequirements.length
            ? mp.physioRequirements
            : resolvedServiceRole === "physio"
              ? specializations
              : [],
        workTypes: boundWorkTypes,
      },
      professionalSpecializations: specializations,
      physioDossier: ext.physioDossier || base.profileExtended.physioDossier,
    },
  };
}
